import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const HomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1000px;
  margin: 0 auto;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const WelcomeSection = styled.section`
  margin-bottom: 40px;
  text-align: center;
  width: 100%;
`;

const HomeTitle = styled.h1`
  text-align: center;
  color: #2c3e50;
  font-family: 'Cormorant Garamond', serif;
  font-size: 2.8rem;
  letter-spacing: 5px;
  margin: 40px 0 20px;
  position: relative;
  padding: 25px 0;
  font-weight: 400;
  text-transform: uppercase;
  animation: titleFadeIn 1.5s ease-out forwards;
  
  @keyframes titleFadeIn {
    0% {
      opacity: 0;
      transform: scale(1.3);
      letter-spacing: 8px;
    }
    100% {
      opacity: 1;
      transform: scale(1);
      letter-spacing: 5px;
    }
  }
  
  &::before, &::after {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    height: 1px;
    background: linear-gradient(to right, transparent, #c19b7c, transparent);
    width: 280px;
    animation: lineExpand 2s ease-out;
    
    @keyframes lineExpand {
      0% {
        width: 0;
        opacity: 0;
      }
      50% {
        opacity: 1;
      }
      100% {
        width: 280px;
        opacity: 1;
      }
    }
  }
  
  &::before {
    top: 0;
  }
  
  &::after {
    bottom: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    letter-spacing: 3px;
    padding: 20px 0;
    margin: 30px 0 15px;
    
    &::before, &::after {
      width: 80%;
      max-width: 240px;
      
      @keyframes lineExpand {
        0% {
          width: 0;
          opacity: 0;
        }
        50% {
          opacity: 1;
        }
        100% {
          width: 80%;
          max-width: 240px;
          opacity: 1;
        }
      }
    }
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
    letter-spacing: 2px;
  }
`;

const NameSpan = styled.span`
  display: block;
  font-family: 'Great Vibes', cursive;
  font-size: 3.2rem;
  letter-spacing: 1px;
  font-style: normal;
  text-transform: none;
  margin-top: 10px;
  color: #8d614a;
  animation: nameAppear 2s ease-out;
  
  @keyframes nameAppear {
    0% {
      opacity: 0;
      transform: scale(1.5) translateY(-20px);
    }
    40% {
      opacity: 0;
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 2.4rem;
  }
`;

// Update DateText for mobile
const DateText = styled.p`
  font-size: 1.5rem;
  margin: 20px 0 30px;
  color: #7c5a48;
  font-family: 'Cormorant Garamond', serif;
  letter-spacing: 2px;
  opacity: 0;
  animation: fadeIn 1s ease-out forwards 1.5s;
  text-align: center;
  
  span {
    display: inline-block;
    margin: 0 10px;
  }
  
  .date::after, .time::after {
    content: "•";
    margin-left: 10px;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin: 15px 0 25px;
    
    span {
      margin: 0 5px;
    }
    
    .date::after, .time::after {
      margin-left: 5px;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    letter-spacing: 1px;
    
    span {
      display: block;
      margin: 5px 0;
    }
    
    .date::after, .time::after {
      content: "";
      margin-left: 0;
    }
  }
`;

const InvitationContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 40px;
  margin: 40px 0;
  perspective: 1000px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 30px;
  }
`;

const InvitationCard = styled.div`
  width: 350px;
  border-radius: 8px;
  box-shadow: 0 15px 35px rgba(179, 139, 109, 0.2);
  overflow: hidden;
  transition: all 0.5s ease;
  transform-style: preserve-3d;
  
  &:hover {
    transform: translateY(-10px) rotateY(5deg);
    box-shadow: 0 20px 40px rgba(179, 139, 109, 0.3);
  }
  
  @media (max-width: 768px) {
    width: 90%;
    max-width: 350px;
    margin: 0 auto;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    max-width: 300px;
    
    /* Disable hover effects on mobile */
    &:hover {
      transform: none;
      box-shadow: 0 15px 35px rgba(179, 139, 109, 0.2);
    }
  }
`;


const InvitationImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CTASection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 30px 0 60px;
  text-align: center;
`;

const Message = styled.p`
  font-size: 1.3rem;
  line-height: 1.6;
  margin-bottom: 30px;
  color: #5a4a42;
  max-width: 700px;
  text-align: center;
  font-family: 'Cormorant Garamond', serif;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 0 15px;
    margin-bottom: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    line-height: 1.5;
  }
`;

const CTAButton = styled(Link)`
  padding: 15px 40px;
  background: linear-gradient(to right, #b38b6d, #d4b59e);
  color: white;
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 600;
  border-radius: 4px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(179, 139, 109, 0.3);
  font-family: 'Cormorant Garamond', serif;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  &:hover {
    background: linear-gradient(to right, #a07a5f, #c4a58e);
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(179, 139, 109, 0.4);
  }
  
  @media (max-width: 768px) {
    padding: 12px 30px;
    font-size: 1.1rem;
    letter-spacing: 1.5px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 25px;
    font-size: 1rem;
    letter-spacing: 1px;
    width: 80%;
    text-align: center;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(to right, transparent, rgba(179, 139, 109, 0.3), transparent);
  margin: 30px 0;
`;

const CountdownContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 25px;
  margin: 30px auto 50px;
  max-width: 500px;
  width: 100%;
  
  @media (max-width: 600px) {
    gap: 10px;
    margin: 20px auto 40px;
  }
  
  @media (max-width: 480px) {
    gap: 5px;
  }
`;

const CountdownItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
  position: relative;
  
  &:not(:last-child)::after {
    content: ":";
    position: absolute;
    right: -15px;
    top: 10px;
    font-size: 2rem;
    color: #b38b6d;
    opacity: 0.7;
  }
  
  @media (max-width: 600px) {
    min-width: 60px;
    
    &:not(:last-child)::after {
      right: -8px;
      font-size: 1.8rem;
    }
  }
  
  @media (max-width: 480px) {
    min-width: 50px;
    
    &:not(:last-child)::after {
      right: -5px;
      font-size: 1.5rem;
      top: 8px;
    }
  }
`;

const CountdownNumber = styled.div`
  font-size: 2.8rem;
  font-weight: 600;
  color: #b38b6d;
  font-family: 'Cormorant Garamond', serif;
  background: linear-gradient(135deg, #b38b6d, #d4b59e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 10px rgba(179, 139, 109, 0.2);
  padding: 5px 0;
  
  @media (max-width: 600px) {
    font-size: 2.2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const CountdownLabel = styled.div`
  font-size: 0.95rem;
  color: #947060;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 600;
  margin-top: 5px;
  position: relative;
  
  &::after {
    content: "";
    position: absolute;
    width: 30px;
    height: 1px;
    background: linear-gradient(to right, transparent, #d4b59e, transparent);
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
  }
  
  @media (max-width: 600px) {
    font-size: 0.8rem;
    letter-spacing: 1px;
    
    &::after {
      width: 20px;
      bottom: -5px;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    letter-spacing: 0.5px;
  }
`;

const CountdownWrapper = styled.div`
  background: rgba(255, 255, 255, 0.5);
  border-radius: 15px;
  padding: 20px 30px;
  box-shadow: 0 5px 20px rgba(179, 139, 109, 0.1);
  border: 1px solid rgba(179, 139, 109, 0.1);
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  opacity: 0;
  animation: fadeIn 1s ease-out forwards 2s;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 15px;
    max-width: 95%;
  }
`;

const AnimatedPhoto = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  margin: 20px auto;
  display: block;
  object-fit: cover;
  border: 3px solid #d4b59e;
  box-shadow: 0 5px 15px rgba(179, 139, 109, 0.3);
  animation: fadeIn 1.5s ease-out forwards;
  
  @media (max-width: 768px) {
    width: 130px;
    height: 130px;
  }
  
  @media (max-width: 480px) {
    width: 110px;
    height: 110px;
    margin: 15px auto;
  }
`;


const Home = () => {
  // Calculate days until wedding (October 25, 2025)
  const calculateTimeLeft = () => {
    const weddingDate = new Date('2025-10-25T16:00:00-07:00').getTime();
    const now = new Date().getTime();
    const difference = weddingDate - now;
    
    if (difference > 0) {
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      return { days, hours, minutes, seconds };
    }
    
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  };
  
  const [timeLeft, setTimeLeft] = React.useState(calculateTimeLeft());
  
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    
    return () => clearTimeout(timer);
  });
  
  return (
    <>
      <Helmet>
        <title>Home | Erika and Andrew's Wedding</title>
      </Helmet>
      <HomeContainer>
      <WelcomeSection>
        <HomeTitle>
            We're Getting Married
            <NameSpan>Erika & Andrew</NameSpan>
        </HomeTitle>
        <AnimatedPhoto 
            src="/img/animated_us.png" 
            alt="Erika and Andrew"
            loading="eager"
        />
        <DateText>
            <span className="date">October 25, 2025</span>
            <span className="time">4:00 PM</span>
            <span className="location">Anaheim, California</span>
        </DateText>
        
        <CountdownWrapper>
            <CountdownContainer>
            <CountdownItem>
                <CountdownNumber>{timeLeft.days}</CountdownNumber>
                <CountdownLabel>Days</CountdownLabel>
            </CountdownItem>
            <CountdownItem>
                <CountdownNumber>{timeLeft.hours}</CountdownNumber>
                <CountdownLabel>Hours</CountdownLabel>
            </CountdownItem>
            <CountdownItem>
                <CountdownNumber>{timeLeft.minutes}</CountdownNumber>
                <CountdownLabel>Minutes</CountdownLabel>
            </CountdownItem>
            <CountdownItem>
                <CountdownNumber>{timeLeft.seconds}</CountdownNumber>
                <CountdownLabel>Seconds</CountdownLabel>
            </CountdownItem>
            </CountdownContainer>
        </CountdownWrapper>
        </WelcomeSection>
        
        <InvitationContainer>
          <InvitationCard>
            <InvitationImage src="/img/invitation-front.png" alt="Wedding Invitation Front" />
          </InvitationCard>
          <InvitationCard>
            <InvitationImage src="/img/invitation-back.png" alt="Wedding Invitation Back" />
          </InvitationCard>
        </InvitationContainer>
        
        <Divider />
        
        <CTASection>
          <Message>
            We are thrilled to celebrate our special day with our friends and family. 
            Please join us for this momentous occasion as we begin our journey together as husband and wife.
          </Message>
          <CTAButton to="/rsvp">RSVP Now</CTAButton>
        </CTASection>
      </HomeContainer>
    </>
  );
};

export default Home;